import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 */

/**
 * A custom element that manages a sticky Buy It Now bar.
 * Shows after the customer scrolls more than 200px.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = [
    'stickyBar',
    'addToCartButton',
    'quantityDisplay',
    'quantityNumber'
  ];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;

    const target = this.closest('.shopify-section');

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal }
    );

    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#handleVariantSelected,
      { signal }
    );

    document.addEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartAddComplete,
      { signal }
    );

    document.addEventListener(
      ThemeEvents.cartError,
      this.#handleCartAddComplete,
      { signal }
    );

    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handleQuantityUpdate,
      { signal }
    );

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();

    this.#abortController.abort();

    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }
  }

  /**
   * Sets up the scroll listener and footer observer.
   *
   * Sticky Buy It Now appears after scrolling more than 200px.
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();

    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');

    if (!buyButtonsBlock) return;

    // Find footer
    const footer =
      document.querySelector('footer') ??
      document.querySelector('[class*="footer-group"]');

    if (!footer) return;

    /**
     * Show Sticky Buy It Now after scrolling more than 200px.
     */
    const handleScroll = () => {
      if (window.scrollY > 200) {
        if (!this.#hiddenByBottom) {
          this.#showStickyBar();
        }
      } else {
        this.#hideStickyBar();
      }
    };

    window.addEventListener(
      'scroll',
      handleScroll,
      {
        passive: true,
        signal: this.#abortController.signal
      }
    );

    // Check the current scroll position immediately
    handleScroll();

    /**
     * Footer observer.
     *
     * Hide the sticky button when the customer reaches
     * the bottom area of the product page.
     */
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();

        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          this.#hiddenByBottom = false;

          if (window.scrollY > 200) {
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px'
      }
    );

    this.#mainBottomObserver.observe(footer);

    /**
     * Find the real Add to Cart button.
     *
     * Sticky Buy It Now will trigger this button,
     * allowing the theme to keep handling:
     *
     * - Variant
     * - Quantity
     * - Inventory
     * - Selling Plan
     * - Other product purchase logic
     */
    this.#targetAddToCartButton =
      productForm.querySelector('[ref="addToCartButton"]');
  }

  // =========================================================
  // Buy It Now
  // =========================================================

  /**
   * Handles the Sticky Buy It Now button click.
   *
   * The currently selected product variant and quantity
   * are added to the cart using the theme's original
   * Add to Cart logic.
   *
   * After the cart update succeeds, the customer is
   * redirected directly to Shopify Checkout.
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) {
      console.error(
        'Buy It Now: Add to Cart button not found.'
      );

      return;
    }

    const targetButton = this.#targetAddToCartButton;

    /*
     * Create the event listener BEFORE clicking
     * the original Add to Cart button.
     *
     * This prevents a race condition where Shopify
     * could finish the cart update before our listener
     * is attached.
     */
    const waitForCartUpdate = new Promise((resolve, reject) => {
      let finished = false;

      const cleanup = () => {
        document.removeEventListener(
          ThemeEvents.cartUpdate,
          handleSuccess
        );

        document.removeEventListener(
          ThemeEvents.cartError,
          handleError
        );

        clearTimeout(timeout);
      };

      const handleSuccess = () => {
        if (finished) return;

        finished = true;

        cleanup();

        resolve(true);
      };

      const handleError = (event) => {
        if (finished) return;

        finished = true;

        cleanup();

        reject(
          new Error(
            event?.detail?.error ||
            'Unable to add product to cart.'
          )
        );
      };

      const timeout = setTimeout(() => {
        if (finished) return;

        finished = true;

        cleanup();

        reject(
          new Error(
            'Cart update timed out.'
          )
        );
      }, 10000);

      document.addEventListener(
        ThemeEvents.cartUpdate,
        handleSuccess
      );

      document.addEventListener(
        ThemeEvents.cartError,
        handleError
      );
    });

    /*
     * Tell the theme that the real Add to Cart button
     * is being controlled by the Sticky Buy It Now button.
     */
    targetButton.dataset.puppet = 'true';

    this.dataset.buyNow = 'true';

    /*
     * Click the original Add to Cart button.
     *
     * This preserves the theme's existing logic for:
     *
     * - Selected Variant
     * - Quantity
     * - Inventory
     * - Selling Plan
     * - Product options
     */
    targetButton.click();

    try {
      /*
       * Wait until Shopify/theme confirms
       * that the product was successfully added.
       */
      await waitForCartUpdate;

      /*
       * Directly open Shopify Checkout.
       *
       * Customer does NOT need to visit the Cart page.
       */
      window.location.href = '/checkout';

    } catch (error) {
      console.error(
        'Buy It Now error:',
        error
      );

      /*
       * Restore the original Add to Cart button state.
       */
      targetButton.dataset.puppet = 'false';

      this.dataset.buyNow = 'false';

      this.refs.addToCartButton.removeAttribute(
        'data-added'
      );
    }
  };

  // =========================================================
  // Variant Update
  // =========================================================

  /**
   * Handles variant update events.
   *
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (
      event.detail.data.productId !==
      this.dataset.productId
    ) {
      return;
    }

    const variant = event.detail.resource;

    // Get the new Sticky Add to Cart HTML
    const newStickyAddToCart =
      event.detail.data.html.querySelector(
        'sticky-add-to-cart'
      );

    if (!newStickyAddToCart) return;

    const newStickyBar =
      newStickyAddToCart.querySelector(
        '[ref="stickyBar"]'
      );

    if (!newStickyBar) return;

    /*
     * Store current visibility state before morphing.
     */
    const currentStuck =
      this.refs.stickyBar.getAttribute(
        'data-stuck'
      ) || 'false';

    const variantAvailable =
      newStickyAddToCart.dataset.variantAvailable;

    /*
     * Update the Sticky Bar content.
     */
    morph(
      this.refs.stickyBar,
      newStickyBar,
      {
        childrenOnly: true
      }
    );

    /*
     * Restore visibility state.
     */
    this.refs.stickyBar.setAttribute(
      'data-stuck',
      currentStuck
    );

    this.dataset.variantAvailable =
      variantAvailable;

    /*
     * Update current variant ID.
     */
    if (variant && variant.id) {
      this.dataset.currentVariantId =
        variant.id;
    }

    /*
     * Re-cache the real Add to Cart button.
     *
     * This is important because changing a variant
     * can cause the product form to be updated.
     */
    const productForm =
      this.#getProductForm();

    if (productForm) {
      this.#targetAddToCartButton =
        productForm.querySelector(
          '[ref="addToCartButton"]'
        );
    }

    /*
     * Handle unavailable variant.
     */
    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    /*
     * Update quantity display.
     */
    this.#updateButtonText();
  };

  // =========================================================
  // Variant Selected
  // =========================================================

  /**
   * Handles variant selected events.
   *
   * @param {CustomEvent} event
   */
  #handleVariantSelected = (event) => {
    const variantId =
      event.detail.resource?.id;

    if (!variantId) return;

    this.dataset.currentVariantId =
      variantId;
  };

  // =========================================================
  // Variant Unavailable
  // =========================================================

  /**
   * Updates the variant title when the selected
   * variant is unavailable.
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';

    const variantTitleElement =
      this.querySelector(
        '.sticky-add-to-cart__variant'
      );

    const productId =
      this.dataset.productId;

    const variantPicker =
      document.querySelector(
        `variant-picker[data-product-id="${productId}"]`
      );

    if (
      !variantTitleElement ||
      !variantPicker
    ) {
      return;
    }

    const selectedOptions =
      Array.from(
        variantPicker.querySelectorAll(
          'input:checked'
        )
      )
        .map(
          (option) =>
            /** @type {HTMLInputElement} */
            (option).value
        )
        .filter(
          (value) => value !== ''
        )
        .join(' / ');

    if (!selectedOptions) return;

    variantTitleElement.textContent =
      selectedOptions;
  };

  // =========================================================
  // Cart Add Complete
  // =========================================================

  /**
   * Handles cart add complete.
   *
   * @param {CustomEvent} _event
   */
  #handleCartAddComplete = (_event) => {
    /*
     * Reset the puppet flag after cart operation.
     */
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet =
        'false';
    }
  };

  // =========================================================
  // Quantity Update
  // =========================================================

  /**
   * Handles quantity selector update events.
   *
   * @param {QuantitySelectorUpdateEvent} event
   */
  #handleQuantityUpdate = (event) => {
    /*
     * Ignore cart drawer quantity changes.
     *
     * Only respond to product page quantity changes.
     */
    if (event.detail.cartLine) return;

    this.#currentQuantity =
      event.detail.quantity;

    this.#updateButtonText();
  };

  // =========================================================
  // Show Sticky Bar
  // =========================================================

  /**
   * Shows the Sticky Buy It Now bar.
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;

    this.#isStuck = true;

    stickyBar.dataset.stuck = 'true';
  };

  // =========================================================
  // Hide Sticky Bar
  // =========================================================

  /**
   * Hides the Sticky Buy It Now bar.
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;

    this.#isStuck = false;

    stickyBar.dataset.stuck = 'false';
  };

  // =========================================================
  // Get Product Form
  // =========================================================

  /**
   * Gets the product form element.
   *
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId =
      this.dataset.productId;

    if (!productId) return null;

    const sectionElement =
      this.closest(
        '.shopify-section'
      );

    if (!sectionElement) return null;

    const sectionId =
      sectionElement.id.replace(
        'shopify-section-',
        ''
      );

    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  };

  // =========================================================
  // Initial Quantity
  // =========================================================

  /**
   * Gets the initial quantity from
   * the data attribute.
   */
  #getInitialQuantity() {
    this.#currentQuantity =
      parseInt(
        this.dataset.initialQuantity ||
        '1'
      ) || 1;

    this.#updateButtonText();
  };

  // =========================================================
  // Update Button Text
  // =========================================================

  /**
   * Updates the quantity display.
   */
  #updateButtonText() {
    const {
      addToCartButton,
      quantityDisplay,
      quantityNumber
    } = this.refs;

    const available =
      !addToCartButton.disabled;

    /*
     * Update quantity number.
     */
    quantityNumber.textContent =
      this.#currentQuantity.toString();

    /*
     * Only display quantity when
     * quantity is greater than 1.
     */
    if (
      available &&
      this.#currentQuantity > 1
    ) {
      quantityDisplay.style.display =
        'inline';
    } else {
      quantityDisplay.style.display =
        'none';
    }
  }
}

// =========================================================
// Register Custom Element
// =========================================================

if (
  !customElements.get(
    'sticky-add-to-cart'
  )
) {
  customElements.define(
    'sticky-add-to-cart',
    StickyAddToCartComponent
  );
}
